import React, { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import './Admin.css'; // Import CSS file for styles

const Table = forwardRef(({ 
  rows, 
  columns, 
  columnTypes = [], 
  columnNames = [], 
  data = [], 
  buttonNames, 
  onButtonClick = [], 
  departments = [], // Array of department objects [{ id, name }]
  roles = [], // Array of role objects [{ id, name }]
  onDropdownChange = () => {}, // Callback function for dropdown selection
  showCheckbox = false, // New prop to control if checkbox column is visible
  onRowDoubleClick = () => {} // Callback function for row double-click
}, ref) => {
  const validRows = Math.max(rows, 0);
  const validColumns = Math.max(columns, 0);

  const [tableData, setTableData] = useState(
    data.length > 0 ? data : Array(validRows).fill(null).map(() => Array(validColumns).fill(''))
  );

  const [selectedRows, setSelectedRows] = useState(
    Array(validRows).fill(false) // Initialize selected rows as false
  );

  // Handle input change for other columns
  const handleInputChange = (rowIndex, colIndex, value) => {
    const newTableData = [...tableData];
    newTableData[rowIndex][colIndex] = value;
    setTableData(newTableData);
  };

  // Handle checkbox change
  const handleCheckboxChange = (rowIndex) => {
    const updatedSelectedRows = [...selectedRows];
    updatedSelectedRows[rowIndex] = !updatedSelectedRows[rowIndex]; // Toggle selection
    setSelectedRows(updatedSelectedRows);
  };

  // Expose method to set cell value
  useImperativeHandle(ref, () => ({
    setCellValue: (rowIndex, colIndex, value) => {
      const newTableData = [...tableData];
      if (rowIndex < validRows && colIndex < validColumns) {
        newTableData[rowIndex][colIndex] = value;
        setTableData(newTableData);
      }
    }
  }));

  useEffect(() => {
    if (data.length > 0) {
      setTableData(data);
    }
  }, [data]);

  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            {/* Conditionally render the checkbox column */}
            {showCheckbox && <th>Select</th>}
            {Array(validColumns).fill('').map((_, idx) => (
              <th key={idx}>{columnNames[idx] || `Column ${idx + 1}`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array(validRows).fill().map((_, rowIndex) => (
            <tr
              key={rowIndex}
              className="table-row"
              onDoubleClick={() => onRowDoubleClick(rowIndex)} // Handle double-click event
            >
              {/* Conditionally render the checkbox in the row */}
              {showCheckbox && (
                <td>
                  <input 
                    type="checkbox" 
                    checked={selectedRows[rowIndex]} 
                    onChange={() => handleCheckboxChange(rowIndex)} 
                  />
                </td>
              )}
              {Array(validColumns).fill().map((_, colIndex) => {
                const columnType = columnTypes[colIndex] || 'text'; // Default type is text
                const assignedValue = tableData[rowIndex]?.[colIndex] || ""; // Current assigned value

                return (
                  <td key={colIndex}>
                    {columnType === 'button' ? (
                      <button 
                        style={{ width: '100%' }} 
                        onClick={() => {
                          const buttonIndex = validColumns - 1 - colIndex;
                          if (onButtonClick[buttonIndex]) {
                            onButtonClick[buttonIndex](rowIndex);
                          }
                        }}
                        disabled={data[rowIndex]?.[colIndex]?.disabled}
                      >
                        {buttonNames ? buttonNames[validColumns - 1 - colIndex] || 'View' : 'Apply'}
                      </button>
                    ) : columnType === 'dropdown' ? (
                      assignedValue ? (
                        <span>{assignedValue}</span>
                      ) : (
                        <select
                          value={assignedValue}
                          onChange={(e) => {
                            // Immediately update the table data
                            handleInputChange(rowIndex, colIndex, e.target.value);
                          }}
                          className="table-dropdown"
                        >
                          <option value="">Select</option>
                          {(colIndex === 2 ? departments : colIndex === 3 ? roles : []).map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      )
                    ) : columnType === 'date' ? (
                      <input
                        type="date"
                        value={assignedValue}
                        onChange={(e) => handleInputChange(rowIndex, colIndex, e)}
                        className="table-input"
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <input
                        type={columnType} // e.g., 'text', 'date', etc.
                        value={assignedValue} // Ensure this is the state value bound to the field
                        onChange={(e) => handleInputChange(rowIndex, colIndex, e.target.value)} // Handle change to update state
                        className="table-input"
                        style={{ width: '100%' }}
                        placeholder='hi' // You can customize the placeholder
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default Table;

